import { useRef } from 'react';
import { KeenIcon } from '@/components/keenicons';
import { toAbsoluteUrl } from '@/utils';
import { Menu, MenuItem, MenuToggle } from '@/components';
import { DropdownUser } from '@/partials/dropdowns/user';
import { useLanguage } from '@/i18n';
import { IMenuItemRef } from '@/components/menu';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useGetUnreadNotificationsQuery } from '@/services/NotificationService';
import { useNavigate } from 'react-router-dom';
import { NOTIFICATIONS_POLLING_INTERVAL } from '@/constant/notifications';

const HeaderTopbar = () => {
  const { isRTL } = useLanguage();
  const itemUserRef = useRef<IMenuItemRef>(null);
  const itemNotificationsRef = useRef<IMenuItemRef>(null);

  const profile_pic = useSelector(
    (state: RootState) => state.auth.profile_picture
  );

  const navigate = useNavigate();

  const { data: unreadNotifications } = useGetUnreadNotificationsQuery(
    { limit: 100, page: 1 },
    { pollingInterval: NOTIFICATIONS_POLLING_INTERVAL }
  );

  const unreadCount = unreadNotifications?.total_items || 0;
  const displayUnreadCount = unreadCount > 99 ? '99+' : `${unreadCount}`;

  return (
    <nav
      className="flex items-center gap-2 lg:gap-3.5"
      aria-label="Header actions"
    >
      <Menu>
        <MenuItem
          ref={itemNotificationsRef}
          toggle="dropdown"
          trigger="click"
          dropdownProps={{
            placement: isRTL() ? 'bottom-start' : 'bottom-end',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: isRTL() ? [70, 10] : [-70, 10], // [skid, distance]
                },
              },
            ],
          }}
        >
          <button
            className="btn btn-icon btn-icon-lg relative cursor-pointer size-9 rounded-full hover:bg-primary-light hover:text-primary dropdown-open:bg-primary-light dropdown-open:text-primary text-gray-500 contrast:text-white contrast:hover:text-white contrast:dropdown-open:text-white"
            aria-label={
              unreadCount > 0
                ? `Notifications, ${displayUnreadCount} unread`
                : 'Notifications'
            }
            title="Notifications"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate('/notification-management');
            }}
          >
            <KeenIcon icon="notification-status" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center h-5 min-w-[1.25rem] px-1"
                aria-label={`${displayUnreadCount} unread`}
                role="status"
              >
                {displayUnreadCount}
              </span>
            )}
          </button>
        </MenuItem>
      </Menu>

      <Menu>
        <MenuItem
          ref={itemUserRef}
          toggle="dropdown"
          trigger="click"
          dropdownProps={{
            placement: isRTL() ? 'bottom-start' : 'bottom-end',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: isRTL() ? [-20, 10] : [20, 10], // [skid, distance]
                },
              },
            ],
          }}
        >
          <MenuToggle
            ariaLabel="User profile"
            className="btn btn-icon rounded-full"
          >
            <img
              className="size-9 rounded-full border-2 border-success shrink-0"
              src={
                (profile_pic as string) ||
                toAbsoluteUrl('/media/avatars/defult_avtar.png')
              }
              alt="Admin user profile picture"
              role="img"
              loading="lazy"
            />
          </MenuToggle>
          {DropdownUser({ menuItemRef: itemUserRef })}
        </MenuItem>
      </Menu>
    </nav>
  );
};

export { HeaderTopbar };
